'use client';

import { Card, Label } from '@repo/ui';
import { ScrollableContainer } from '../common/scrollable-container';
import { DistributionGraph } from '../common/distribution-graph';
import { StatisticalSummaryResult } from '../../../lib/stats/statistical-summary';

interface StatisticalSummaryCalculatorProps {
  visualizationData: StatisticalSummaryResult | null;
}

export function StatisticalSummaryCalculator({ visualizationData: result }: StatisticalSummaryCalculatorProps) {
  if (!result) {
    return null;
  }

  const distributionLabels = [
    { value: result.extremes.min, label: 'Min', color: '#E57373' },
    { value: result.quartiles.q1, label: 'Q1', color: '#81C784' },
    { value: result.centralTendency.mean, label: 'Mean', color: '#4CAF50' },
    { value: result.quartiles.q2, label: 'Median', color: '#2196F3' },
    { value: result.quartiles.q3, label: 'Q3', color: '#81C784' },
    { value: result.extremes.max, label: 'Max', color: '#E57373' }
  ];

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Central Tendency</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Mean</Label>
              <div className="text-lg font-semibold">
                {result.centralTendency.mean.toFixed(2)}
              </div>
            </div>
            <div>
              <Label>Median</Label>
              <div className="text-lg font-semibold">
                {result.centralTendency.median.toFixed(2)}
              </div>
            </div>
            <div>
              <Label>Mode</Label>
              <div className="text-lg font-semibold">
                {result.centralTendency.mode.map(m => m.toFixed(2)).join(', ')}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Dispersion Measures</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Range</Label>
              <div className="text-lg font-semibold">
                {result.dispersion.range.toFixed(2)}
              </div>
            </div>
            <div>
              <Label>IQR</Label>
              <div className="text-lg font-semibold">
                {result.dispersion.iqr.toFixed(2)}
              </div>
            </div>
            <div>
              <Label>Population Std Dev</Label>
              <div className="text-lg font-semibold">
                {result.dispersion.standardDeviation.population.toFixed(2)}
              </div>
            </div>
            <div>
              <Label>Sample Std Dev</Label>
              <div className="text-lg font-semibold">
                {result.dispersion.standardDeviation.sample.toFixed(2)}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Distribution Shape</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Skewness</Label>
              <div className="text-lg font-semibold">
                {result.distribution.skewness.toFixed(2)}
              </div>
            </div>
            <div>
              <Label>Kurtosis</Label>
              <div className="text-lg font-semibold">
                {result.distribution.kurtosis.toFixed(2)}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Summary Counts</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Total Values</Label>
              <div className="text-lg font-semibold">
                {result.count.total}
              </div>
            </div>
            <div>
              <Label>Unique Values</Label>
              <div className="text-lg font-semibold">
                {result.count.unique}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Interpretation</h3>
          <div className="text-sm text-muted-foreground">
            {result.interpretation}
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Distribution Plot</h3>
          <DistributionGraph
            data={result.data}
            height={300}
            labels={distributionLabels.map(({ value, label, color }) => ({
              value,
              text: label,
              color,
              lineStyle: label === 'Mean' || label === 'Median' ? 'solid' : 'dashed'
            }))}
            grid={{
              left: '15%',
              right: '5%',
              top: '10%',
              bottom: '15%'
            }}
          />
        </Card>
      </div>
    </ScrollableContainer>
  );
} 